import React, { useCallback, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { ArrowLeft } from "react-feather";
import DatePicker, { CalendarContainer } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { displayToast } from "../utils/Toast";
import { useErrorHandler } from "../services/ErrorHandler";
import Edit from "../assets/svgIcons/FormEdit.svg";
import Delete from "../assets/svgIcons/FormDelete.svg";
import ConfirmationModal from "../components/ConfirmationModal";
import { capitalizeFirstLetterOfEachWord } from "../utils/Formatter";
import { getFieldMaster } from "../apis/fieldMaster";
import {
  insertMasterTransaction,
  fetchTransactionById,
  editMasterTransaction,
  insertProductMaster,
  editProductMaster,
  fetchTransactionPopupData,
  deleteTransactionByCode,
} from "../apis/transaction";
import { useSalesFormStoreApi } from "../services/useSalesFormStore";
import { usePermissions } from "../hooks/usePermissions";
import useAutoRefresh from "../hooks/useAutoRefresh";
import { uploadFile } from "../utils/uploadFile";
import { useMode } from "../context/ModeContext";
import { resolveValidationFieldUpdate } from "../utils/validationFieldUpdate";
import "../styles/components/MasterDataModal.css";

const ClearableCalendar = ({ children, onClear, className, ...props }) => {
  const [dropdownRow, setDropdownRow] = useState(null);
  const calendarRef = useCallback((node) => {
    setDropdownRow(
      node?.querySelector(".react-datepicker__header__dropdown") ?? null,
    );
  }, []);

  return (
    <div ref={calendarRef}>
      <CalendarContainer
        {...props}
        className={`${className || ""} clearable-datepicker-calendar`}
      >
        {children}
      </CalendarContainer>
      {dropdownRow &&
        onClear &&
        createPortal(
          <button
            type="button"
            className="react-datepicker__month-read-view calendar-clear-button"
            onClick={onClear}
          >
            Clear
          </button>,
          dropdownRow,
        )}
    </div>
  );
};

const MasterDataModal = ({
  transactionCode: propTransactionCode,
  heading = "",
  selectedDetail = "",
  selectedDetailData = {},
  modeOfEdit = "",
  onClose,
  extraForms,
  newEntryTranCode,
  isPopupMode = false,
}) => {
  const location = useLocation();
  const salesFormStore = useSalesFormStoreApi();
  const { setMode } = useMode();
  const { tab: urlTab, category, mode } = useParams();
  const navigate = useNavigate();
  const { handleErrorResponse } = useErrorHandler();
  const drawingInputRef = useRef(null);
  const qcInputRef = useRef(null);

  const tab =
    urlTab ||
    (window.location.pathname.startsWith("/master") ? "master" : undefined);

  const transactionCode = (() => {
    if (propTransactionCode) {
      return propTransactionCode;
    }
    if (location.state?.transactionCode) {
      return location.state.transactionCode;
    }
    if (heading === "otherDetailsModal") {
      return location.state?.transactionCode;
    }
    return null;
  })();

  const displayCategory =
    transactionCode === "CM"
      ? "customer"
      : transactionCode === "VM"
        ? "vendor"
        : transactionCode === "PM"
          ? "new Product"
          : category;

  const transId = location.state?.transId;
  const colorCode = location.state?.colorCode;
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState([]);
  const [currentMode, setCurrentMode] = useState(mode);
  useAutoRefresh(() => fetchTransactionData(), {
    enabled: currentMode === "view" && Boolean(transId),
  });
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const [openDropdownField, setOpenDropdownField] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const rights = usePermissions(transactionCode);

  const [drawingUrl, setDrawingUrl] = useState("");
  const [inspectionFormatUrl, setInspectionFormatUrl] = useState("");

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    try {
      const response = await uploadFile(file, type);
      const fileUrl = response?.url || "";

      if (type === "drawing") {
        setDrawingUrl(fileUrl);
      } else if (type === "inspection_format") {
        setInspectionFormatUrl(fileUrl);
      }
    } catch (err) {
      displayToast("Upload failed", err);
    }
  };

  const detailButtons =
    extraForms?.length > 0
      ? extraForms.map((form) =>
          capitalizeFirstLetterOfEachWord(form.label || form.title || ""),
        )
      : [];

  useEffect(() => {
    if (heading === "otherDetailsModal" && Array.isArray(selectedDetail)) {
      if (selectedDetailData && Object.keys(selectedDetailData).length > 0) {
        setFormData(selectedDetailData);
      } else {
        const { otherDetailsData } = salesFormStore.getState();
        const initialData = selectedDetail.reduce((acc, field) => {
          const existingData = otherDetailsData.find(
            (detail) => detail[field.field_name] !== undefined,
          );
          acc[field.field_name] = existingData
            ? existingData[field.field_name]
            : "";
          return acc;
        }, {});
        setFormData(initialData);
      }
    }
  }, [selectedDetail, selectedDetailData, heading, salesFormStore]);

  useEffect(() => {
    if (currentMode === "add") {
      setMode("add");
    } else if (currentMode === "edit") {
      setMode("edit");
    } else {
      setMode("view");
    }
  }, [setMode, currentMode]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".modal-dropdown-container")) {
        setOpenDropdownField(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      if (heading === "otherDetailsModal") {
        setFields(selectedDetail);
      } else {
        await fetchFieldMaster();
        if (mode === "view" && transId) {
          await fetchTransactionData();
        }
      }
    };
    initializeData();
  }, [transactionCode, transId, heading]);

  const fetchTransactionData = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await fetchTransactionById(transactionCode, transId);
      if (response.description) {
        displayToast(response.description, "error");
      } else {
        setTransactionData(response);
        if (response.head_data) {
          setFormData(response.head_data);

          const { drawing_url, inspection_format_url } = response.head_data;
          drawing_url && setDrawingUrl(drawing_url);
          inspection_format_url &&
            setInspectionFormatUrl(inspection_format_url);
        }
      }
    } catch (error) {
      displayToast("Failed to fetch transaction data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  function normalizePathValue(value) {
    if (!value) return "";

    const decoded = decodeURIComponent(value.trim().toLowerCase());

    const exceptions = ["sales"];

    let normalizedValue = decoded;

    if (!exceptions.includes(decoded)) {
      normalizedValue = decoded.endsWith("s") ? decoded.slice(0, -1) : decoded;
    }

    normalizedValue = normalizedValue.replace(/\s+/g, "");
    const finalValue =
      normalizedValue.charAt(0).toUpperCase() +
      normalizedValue.slice(1).toLowerCase();

    return finalValue;
  }

  const fetchFieldMaster = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await getFieldMaster({
        transactionCode: transactionCode,
      });
      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        let validFields = (response.items?.head_fields || [])
          .filter((field) => !field.is_internal_field)
          .sort((a, b) => a.order_no - b.order_no);

        if (isPopupMode) {
          validFields = validFields.filter((field) => field.is_required);
        }

        setFields(validFields);

        if (mode !== "view") {
          const pathParts = window.location.pathname.split("/").filter(Boolean);
          const thirdItem = pathParts[2] || null;

          const decodedValue = thirdItem ? normalizePathValue(thirdItem) : "";

          const initialFormData = validFields.reduce((acc, field) => {
            acc[field.field_name] = "";
            return acc;
          }, {});

          if (decodedValue) {
            initialFormData.type = decodedValue;
          }
          setFormData(initialFormData);
        }
      }
    } catch (error) {
      displayToast("Failed to fetch field master data", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (value === "Add New +") {
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEnterValidation = async (field) => {
    if (!field?.enter_validation) return;

    if (field.enter_validation.is_db_query && field.enter_validation.db_query) {
      try {
        const response = await fetchTransactionPopupData(
          field.enter_validation.db_query,
        );

        if (response.items && field.enter_validation.field_updation) {
          const updatedFields = {};

          field.enter_validation.field_updation.forEach((update) => {
            const { copy_to_table_type } = update;

            if (copy_to_table_type === "head") {
              const resolvedUpdate = resolveValidationFieldUpdate(update, {
                db: response.items?.[0] || {},
                head: formData || {},
              });

              if (resolvedUpdate?.targetField) {
                updatedFields[resolvedUpdate.targetField] =
                  resolvedUpdate.value;
              }
            }
          });

          if (Object.keys(updatedFields).length > 0) {
            setFormData((prev) => ({
              ...prev,
              ...updatedFields,
            }));
          }
        }
      } catch (error) {
        console.error("Enter Validation Error:", error);
      }
    }
  };

  const handleLeaveValidation = async (field) => {
    if (!field?.leave_validation) return;

    if (field.leave_validation.is_db_query && field.leave_validation.db_query) {
      try {
        const response = await fetchTransactionPopupData(
          field.leave_validation.db_query,
        );

        if (response.items && field.leave_validation.field_updation) {
          const updatedFields = {};

          field.leave_validation.field_updation.forEach((update) => {
            const { copy_to_table_type } = update;

            if (copy_to_table_type === "head") {
              const resolvedUpdate = resolveValidationFieldUpdate(update, {
                db: response.items?.[0] || {},
                head: formData || {},
              });

              if (resolvedUpdate?.targetField) {
                updatedFields[resolvedUpdate.targetField] =
                  resolvedUpdate.value;
              }
            }
          });

          if (Object.keys(updatedFields).length > 0) {
            setFormData((prev) => ({
              ...prev,
              ...updatedFields,
            }));
          }
        }
      } catch (error) {
        console.error("Leave Validation Error:", error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsButtonLoading(true);

    if ((currentMode || modeOfEdit) === "add" && !rights.can_add) {
      displayToast(
        `You dont have permission to add ${displayCategory}`,
        "error",
      );
      setIsButtonLoading(false);
      return;
    }

    if (currentMode === "edit" && !rights.can_edit) {
      displayToast(
        `You dont have permission to edit ${displayCategory}`,
        "error",
      );
      setIsButtonLoading(false);
      return;
    }

    const { addOtherDetails, otherDetailsData } = salesFormStore.getState();

    if (heading === "otherDetailsModal" && modeOfEdit === "add") {
      let detailsMap = new Map();

      otherDetailsData.forEach((item) => {
        const key = Object.keys(item)[0];
        detailsMap.set(key, item);
      });

      Object.keys(formData).forEach((key) => {
        detailsMap.set(key, { [key]: formData[key] });
      });

      const updatedDetails = Array.from(detailsMap.values());

      addOtherDetails(updatedDetails);
      displayToast("Other details saved successfully", "success");
      onClose();
      setIsButtonLoading(false);
      return;
    }

    const isAddMode = (currentMode || modeOfEdit) === "add";

    if (isAddMode) {
      const organisation = JSON.parse(localStorage.getItem("organisation"));
      const organisationId = organisation?.id;
      if (!organisationId) {
        displayToast("Organisation ID not found", "error");
        setIsButtonLoading(false);
        return;
      }

      const submissionData = {
        head_data: {
          organisation_id: organisationId,
          ...formData,
          ...otherDetailsData,
          drawing_url: drawingUrl,
          inspection_format_url: inspectionFormatUrl,
          tran_no: "",
        },
      };

      try {
        let response;

        if (transactionCode === "PM") {
          response = await insertProductMaster(submissionData);
        } else {
          response = await insertMasterTransaction(
            submissionData,
            transactionCode,
          );
        }
        if (response?.head_data) {
          displayToast("Transaction added successfully", "success");

          if (modeOfEdit !== "add") {
            navigate(-1);
          } else {
            navigate(`/${tab}/${category}/transactions`, {
              state: {
                company: response.head_data,
                transactionCode: newEntryTranCode,
              },
            });
            onClose();
          }
        } else if (response?.description) {
          handleErrorResponse(response.description);
          displayToast(response.description, "error");
        }
      } catch (error) {
        displayToast("Error adding transaction", "error");
      } finally {
        setIsButtonLoading(false);
      }
    } else if (currentMode === "edit" && transId) {
      try {
        const { bom_created, pl_created, ...filteredFormData } = formData;

        const updatedData = {
          tran_id: transId,
          head_data: {
            ...filteredFormData,
          },
          item_data: [],
        };

        let response;

        if (transactionCode === "PM") {
          response = await editProductMaster(updatedData);
        } else {
          response = await editMasterTransaction(transactionCode, updatedData);
        }

        if (response?.head_data) {
          displayToast("Transaction updated successfully", "success");
          navigate(-1);
        } else if (response.description) {
          handleErrorResponse(response.description);
          displayToast(response.description, "error");
        }
      } catch (error) {
        displayToast("Error updating transaction", "error");
      } finally {
        setIsButtonLoading(false);
      }
    }
  };

  const handleDeleteClick = () => {
    if (!rights.can_delete) {
      displayToast(
        `You don't have permission to delete ${displayCategory}`,
        "error",
      );
      return;
    }
    setShowDeleteConfirmation(true);
  };

  const toggleEditMode = () => {
    if (!rights.can_edit) {
      displayToast(
        `You dont have permission to edit ${displayCategory}`,
        "error",
      );
      return;
    }
    setCurrentMode("edit");
  };

  const handleCloseEditMode = () => {
    setCurrentMode("view");
    if (mode === "view" && transId) {
      fetchTransactionData();
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteTransactionByCode(transactionCode, transId);

      if (response.description) {
        handleErrorResponse(response.description);
        displayToast(response.description, "error");
      } else {
        displayToast("Transaction deleted successfully", "success");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
      displayToast("Failed to delete transaction", "error");
    } finally {
      setShowDeleteConfirmation(false);
    }
  };

  useEffect(() => {
    const fetchAllDropdownOptions = async () => {
      const queryFields = fields.filter(
        (field) =>
          field.data_type === "DROPDOWN" && field.is_query && field.db_query,
      );

      const newOptions = {};

      for (const field of queryFields) {
        try {
          const response = await fetchTransactionPopupData(field.db_query);
          if (response?.items?.length > 0) {
            newOptions[field.field_name] = response.items?.map((item) =>
              typeof item === "object" ? item.category || "" : item,
            );
          } else {
            displayToast(response.message, "error");
          }
        } catch (error) {
          console.error(
            `Error fetching options for ${field.field_name}:`,
            error,
          );
        }
      }

      setDropdownOptions((prev) => ({ ...prev, ...newOptions }));
    };

    fetchAllDropdownOptions();
  }, [fields]);

  const renderField = (field) => {
    if (!field) return null;

    const fieldValue = formData[field.field_name] || "";
    const isReadOnlyMode =
      heading === "otherDetailsModal"
        ? modeOfEdit === "view"
        : currentMode === "view";

    const optionsToRender = field.is_query
      ? dropdownOptions[field.field_name] || []
      : field.options
          ?.split(",")
          .map((opt) => opt.trim())
          .map(
            (opt) => opt.charAt(0).toUpperCase() + opt.slice(1).toLowerCase(),
          );

    if (field.field_type === "DATE") {
      return (
        <div
          className="master-data-modal-form-field"
          key={field.field_name}
          onClick={
            !isReadOnlyMode ? () => handleEnterValidation(field) : undefined
          }
          onBlur={
            !isReadOnlyMode ? () => handleLeaveValidation(field) : undefined
          }
        >
          <label className="master-data-modal-label">
            {field.title}
            {field.is_required && " *"}
          </label>
          <DatePicker
            selected={fieldValue ? new Date(fieldValue) : null}
            onChange={(date) =>
              setFormData((prev) => ({
                ...prev,
                [field.field_name]: date,
              }))
            }
            dateFormat="dd-MM-yyyy"
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={26}
            dropdownMode="scroll"
            calendarContainer={(props) => (
              <ClearableCalendar
                {...props}
                onClear={
                  isReadOnlyMode
                    ? undefined
                    : () =>
                        setFormData((prev) => ({
                          ...prev,
                          [field.field_name]: null,
                        }))
                }
              />
            )}
            className="master-data-modal-input"
            placeholderText={`Select ${field.title.toLowerCase()}`}
            disabled={isReadOnlyMode}
            wrapperClassName="master-data-modal-datepicker-wrapper"
            popperProps={{ strategy: "fixed" }}
            portalId="root-portal"
          />
        </div>
      );
    }

    return (
      <div
        className="master-data-modal-form-field"
        key={field.field_name}
        onClick={
          !isReadOnlyMode ? () => handleEnterValidation(field) : undefined
        }
        onBlur={
          !isReadOnlyMode ? () => handleLeaveValidation(field) : undefined
        }
      >
        <label className="master-data-modal-label">
          {field.title}
          {field.is_required && " *"}
        </label>
        {field.options ? (
          <div className="modal-dropdown-container">
            <div
              className={`customer-data-modal-input ${
                currentMode === "view"
                  ? "non-editable"
                  : "master-data-modal-select-wrapper"
              } ${
                openDropdownField === field.field_name
                  ? "dropdown-open-highlight"
                  : ""
              }`}
              onClick={() =>
                currentMode !== "view" && setOpenDropdownField(field.field_name)
              }
            >
              <span className="modal-dropdown-text">
                {fieldValue || `Select ${field.title}`}
              </span>
              <span className="dropdown-icon">▼</span>
            </div>

            {openDropdownField === field.field_name &&
              currentMode !== "view" && (
                <div className="primary-dropdown-options">
                  {Array.isArray(optionsToRender) &&
                    optionsToRender.map((option, index) => {
                      const isSelected = formData[field.field_name] === option;
                      return (
                        <div
                          key={index}
                          className={`primary-dropdown-option ${
                            isSelected ? "selected-option" : ""
                          }`}
                          onClick={() => {
                            handleInputChange({
                              target: {
                                name: field.field_name,
                                value: option,
                              },
                            });
                            setOpenDropdownField(null);
                          }}
                        >
                          {option}
                        </div>
                      );
                    })}
                </div>
              )}
          </div>
        ) : (
          <input
            type={field.data_type === "int" ? "number" : "text"}
            name={field.field_name}
            className={`master-data-modal-input ${
              (
                heading === "otherDetailsModal"
                  ? modeOfEdit === "view"
                  : currentMode === "view"
              )
                ? "non-editable"
                : " "
            }`}
            placeholder={`Enter ${field.title.toLowerCase()}`}
            value={fieldValue}
            onChange={handleInputChange}
            readOnly={
              heading === "otherDetailsModal"
                ? modeOfEdit === "view"
                : currentMode === "view"
            }
            maxLength={field.field_length}
            required={field.is_required}
          />
        )}
      </div>
    );
  };

  if (isLoading) {
    return <div className="master-data-modal-container">Loading...</div>;
  }

  return (
    <div className="master-data-modal-container">
      <div
        className="master-data-modal-header"
        style={{
          backgroundColor: colorCode ? `#${colorCode}` : "var(--secondary)",
        }}
      >
        <div className="master-data-modal-title-container">
          <h2 className="master-data-modal-title">
            {heading !== "otherDetailsModal"
              ? `${
                  isPopupMode ? "Quick Add " : ""
                }${capitalizeFirstLetterOfEachWord(
                  currentMode,
                )} ${capitalizeFirstLetterOfEachWord(displayCategory)}`
              : `${capitalizeFirstLetterOfEachWord(
                  modeOfEdit,
                )} ${capitalizeFirstLetterOfEachWord(selectedDetail.title)}`}
          </h2>

          {heading === "otherDetailsModal" ? (
            <X
              className="sales-form-icons cursor-pointer"
              onClick={onClose}
              size={24}
              title="Close"
            />
          ) : currentMode === "view" ? (
            <>
              <img
                src={Edit}
                onClick={toggleEditMode}
                className="sales-form-icons"
                alt="Edit"
                style={{
                  cursor: rights.can_edit ? "pointer" : "not-allowed",
                  opacity: rights.can_edit ? 1 : 0.5,
                }}
                title={rights.can_edit ? "Edit" : "No permission to edit"}
              />
              <img
                src={Delete}
                className="sales-form-icons"
                alt="Delete"
                onClick={handleDeleteClick}
                style={{
                  cursor: rights.can_delete ? "pointer" : "not-allowed",
                  opacity: rights.can_delete ? 1 : 0.5,
                  marginLeft: 12,
                }}
                title={
                  rights.can_delete
                    ? "Delete Transaction"
                    : "No permission to delete"
                }
              />
              {window.location.pathname.startsWith("/master") && (
                <button className="back-button-modal" onClick={onClose}>
                  <ArrowLeft size={25} />
                </button>
              )}
            </>
          ) : (
            ((window.location.pathname.startsWith("/master") &&
              currentMode === "add") ||
              currentMode !== "add") && (
              <X
                className="sales-form-icons cursor-pointer"
                onClick={() =>
                  modeOfEdit === "add" ||
                  (window.location.pathname.startsWith("/master") &&
                    currentMode === "add")
                    ? onClose()
                    : handleCloseEditMode()
                }
                size={24}
                title="Close Mode"
              />
            )
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="master-data-modal-form">
        <div
          className={
            heading !== "otherDetailsModal"
              ? "master-data-modal-form-fields"
              : "master-data-modal-field"
          }
        >
          {fields.map((field) => renderField(field))}
        </div>

        {isPopupMode && (
          <div className="popup-mode-indicator">
            <strong>Quick Add Mode:</strong> Only required fields are shown. For
            complete details, use the master menu.
          </div>
        )}

        {heading !== "otherDetailsModal" && detailButtons.length > 0 && (
          <div className="add-extra-details">
            <h6>Other Details *</h6>
            <div className="add-detail-buttons">
              {detailButtons.map((text, index) => (
                <button key={index} type="button" className="add-detail-button">
                  {text}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="master-data-modal-footer">
          {isButtonLoading ? (
            <div className="loader-container">
              <span className="loader"></span>
            </div>
          ) : (
            (currentMode === "add" ||
              currentMode === "edit" ||
              modeOfEdit === "add") && (
              <button
                type="submit"
                className="master-data-modal-save-button"
                style={{
                  cursor: (
                    currentMode === "add" ? rights.can_add : rights.can_edit
                  )
                    ? "pointer"
                    : "not-allowed",
                  opacity: (
                    currentMode === "add" ? rights.can_add : rights.can_edit
                  )
                    ? 1
                    : 0.5,
                }}
              >
                Done
              </button>
            )
          )}
        </div>
      </form>
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this transaction?"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default MasterDataModal;
